import React, { useState } from "react";
import { Edit } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog.js";
import { Button } from "../../../ui/button.js";
import { toast } from "react-toastify";
import axios from "axios";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

const BulkEditDialog = ({ isOpen, onClose, data, trainingOptions, fetchData, setSelectedTrainings, setIsMultiSelectMode }) => {
  const [loading, setLoading] = useState(false);
  const [updateFields, setUpdateFields] = useState({
    startTime: '',
    endTime: '',
    description: '',
    training: '',
    address: ''
  });
  const [enabledFields, setEnabledFields] = useState({
    startTime: false,
    endTime: false,
    description: false,
    training: false,
    address: false
  });

  const trainings = data?.trainings || [];
  const travels = data?.travels || [];

  const handleFieldToggle = (field) => {
    setEnabledFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleBulkUpdate = async () => {
    if (!Object.values(enabledFields).some(enabled => enabled)) {
      toast.error("Please select at least one field to update");
      return;
    }

    setLoading(true);
    try {
      const promises = [];

      // Update trainings
      if (trainings.length > 0) {
        trainings.forEach(training => {
          const updateData = {};
          
          if (enabledFields.startTime && updateFields.startTime) {
            updateData.start_date = new Date(training.start_date || training.planned_date_start);
            const [hours, minutes] = updateFields.startTime.split(':');
            updateData.start_date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            updateData.start_date = updateData.start_date.toISOString();
          }
          
          if (enabledFields.endTime && updateFields.endTime) {
            updateData.end_date = new Date(training.end_date || training.planned_date_end);
            const [hours, minutes] = updateFields.endTime.split(':');
            updateData.end_date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            updateData.end_date = updateData.end_date.toISOString();
          }
          
          if (enabledFields.description && updateFields.description) {
            updateData.description = updateFields.description;
          }
          
          // Only update training type if we have trainings selected and field is enabled
          if (enabledFields.training && updateFields.training && trainings.length > 0) {
            updateData.training_id = updateFields.training;
          }

          if (Object.keys(updateData).length > 0) {
            promises.push(
              axios.patch(`${API_BASE_URL}/user-training-assignments/${training.id}/`, updateData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
              })
            );
          }
        });
      }


      await Promise.all(promises);
      
      toast.success(`Successfully updated ${trainings.length} trainings`);
      setSelectedTrainings([]);
      setIsMultiSelectMode(false);
      fetchData();
      onClose();
      
    } catch (error) {
      console.error('Error updating items:', error);
      toast.error("Failed to update items");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Edit className="h-5 w-5 mr-2 text-brand-500" />
            Bulk Edit - {trainings.length} Trainings
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <p className="text-sm text-slate-600">
            Select the fields you want to update for all selected items. Only checked fields will be modified.
          </p>

          {/* Common fields for both trainings and travels */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-800 border-b pb-1">Common Fields (Both Trainings & Travels)</h4>
            
            {/* Start Time */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={enabledFields.startTime}
                onChange={() => handleFieldToggle('startTime')}
                className="h-4 w-4 text-brand-500"
              />
              <label className="block text-sm text-slate-700 w-24">Start Time</label>
              <input
                type="time"
                className={`flex-1 p-2 text-sm border rounded ${!enabledFields.startTime ? 'bg-steel-100' : ''}`}
                value={updateFields.startTime}
                onChange={(e) => setUpdateFields(prev => ({ ...prev, startTime: e.target.value }))}
                disabled={!enabledFields.startTime}
              />
            </div>

            {/* End Time */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={enabledFields.endTime}
                onChange={() => handleFieldToggle('endTime')}
                className="h-4 w-4 text-brand-500"
              />
              <label className="block text-sm text-slate-700 w-24">End Time</label>
              <input
                type="time"
                className={`flex-1 p-2 text-sm border rounded ${!enabledFields.endTime ? 'bg-steel-100' : ''}`}
                value={updateFields.endTime}
                onChange={(e) => setUpdateFields(prev => ({ ...prev, endTime: e.target.value }))}
                disabled={!enabledFields.endTime}
              />
            </div>

            {/* Description */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={enabledFields.description}
                onChange={() => handleFieldToggle('description')}
                className="h-4 w-4 text-brand-500 mt-2"
              />
              <label className="block text-sm text-slate-700 w-24 mt-2">Description</label>
              <textarea
                className={`flex-1 p-2 text-sm border rounded ${!enabledFields.description ? 'bg-steel-100' : ''}`}
                value={updateFields.description}
                onChange={(e) => setUpdateFields(prev => ({ ...prev, description: e.target.value }))}
                disabled={!enabledFields.description}
                placeholder="Description"
                rows={3}
              />
            </div>
          </div>

          {/* Training-specific fields */}
          {trainings.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-green-700 border-b border-green-200 pb-1">
                Training-specific Fields ({trainings.length} trainings)
              </h4>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={enabledFields.training}
                  onChange={() => handleFieldToggle('training')}
                  className="h-4 w-4 text-green-600"
                />
                <label className="block text-sm text-slate-700 w-24">Training Type</label>
                <select
                  className={`flex-1 p-2 text-sm border rounded ${!enabledFields.training ? 'bg-steel-100' : ''}`}
                  value={updateFields.training}
                  onChange={(e) => setUpdateFields(prev => ({ ...prev, training: e.target.value }))}
                  disabled={!enabledFields.training}
                >
                  <option value="">Select training...</option>
                  {trainingOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.qualification_type.name} - {option.training_center.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-500 ml-7">Note: Training location is handled through training center selection</p>
            </div>
          )}

        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpdate}
            disabled={loading || !Object.values(enabledFields).some(enabled => enabled)}
            className="bg-brand-500 hover:bg-brand-600 text-white shadow-lg shadow-brand-500/20"
          >
            {loading ? "Updating..." : `Update ${trainings.length} trainings`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEditDialog;